const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const intNumber = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })
const decimal2 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const formatBRL = (value: number) => Number.isFinite(value) ? money.format(value) : '—'
export const formatInt = (value: number) => Number.isFinite(value) ? intNumber.format(Math.round(value)) : '—'
export const format2 = (value: number) => Number.isFinite(value) ? decimal2.format(value) : '—'

export function parseBRNumber(value: string, options: { digitsAsCents?: boolean } = {}) {
  const { digitsAsCents = false } = options
  const raw = String(value ?? '').trim()
  if (!raw) return Number.NaN

  if (digitsAsCents && /^\d+$/.test(raw)) {
    const digits = raw.padStart(3, '0')
    const cents = digits.slice(-2)
    const whole = digits.slice(0, -2) || '0'
    return Number(`${whole}.${cents}`)
  }

  const normalized = raw
    .replace(/\s+/g, '')
    .replace(/^R\$/i, '')
    .replace(/\./g, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '')

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

export function parseDateBR(value: string) {
  const raw = String(value ?? '').trim()
  if (!raw) return null

  let year: number
  let month: number
  let day: number

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number)
    year = y
    month = m
    day = d
  } else {
    const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (!match) return null
    day = Number(match[1])
    month = Number(match[2])
    year = Number(match[3])
  }

  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) return null

  return date
}

export function formatDateBR(date: Date | null) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(date)
}

export function diffDaysUTC(start: Date, end: Date) {
  const startUTC = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate())
  const endUTC = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate())
  return Math.round((endUTC - startUTC) / 86_400_000)
}

export function todayUTC() {
  const now = new Date()
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
}

export function todayBRString() {
  return new Intl.DateTimeFormat('pt-BR').format(new Date())
}

type DiscountInput = {
  consumo: number
  leituraAtual: number
  leitura12m: number
  primeiroDesconto: boolean
}

export type DiscountResult =
  | { error: string }
  | {
      eligible: boolean
      desconto?: boolean
      mediaAnual: number
      limite200: number
      fator?: number
      cobrar?: number
      reason?: string
    }

export function calcularDesconto({ consumo, leituraAtual, leitura12m, primeiroDesconto }: DiscountInput): DiscountResult {
  if (![consumo, leituraAtual, leitura12m].every(Number.isFinite)) {
    return { error: 'Preencha todos os campos numéricos com valores válidos.' }
  }
  if (consumo < 0 || leituraAtual < 0 || leitura12m < 0) {
    return { error: 'Os valores não podem ser negativos.' }
  }
  if (leituraAtual < leitura12m) {
    return { error: 'A leitura atual não pode ser menor que a leitura de 12 meses atrás.' }
  }

  const diferenca = leituraAtual - leitura12m
  const mediaAnual = diferenca / 12
  const limite200 = mediaAnual * 3

  if (consumo < 50) {
    return {
      eligible: false,
      mediaAnual,
      limite200,
      reason: 'Consumo abaixo de 50 m³.',
    }
  }

  if (!Number.isFinite(mediaAnual) || mediaAnual < 0) {
    return { error: 'Não foi possível calcular a média anual com os dados informados.' }
  }

  const fator = primeiroDesconto ? 1.5 : 2
  if (consumo <= limite200) {
    return { eligible: true, desconto: false, mediaAnual, limite200, fator }
  }

  return {
    eligible: true,
    desconto: true,
    mediaAnual,
    limite200,
    fator,
    cobrar: Math.round(mediaAnual * fator),
  }
}

type RetroativoInput = {
  dataLeituraCorreta: Date | null
  dataCliente: Date | null
  dataErro: Date | null
  leituraAtual: number
  leituraCorreta: number
}

export type RetroativoResult =
  | { error: string }
  | {
      diasTotal: number
      diasErro: number
      consumoTotal: number
      m3Dia: number
      m3Cobrar: number
      leituraCorrigida: number
    }

export function calcularRetroativo(input: RetroativoInput): RetroativoResult {
  const { dataLeituraCorreta, dataCliente, dataErro, leituraAtual, leituraCorreta } = input
  if (![leituraAtual, leituraCorreta].every(Number.isFinite) || !dataLeituraCorreta || !dataCliente || !dataErro) {
    return { error: 'Preencha datas e leituras com valores válidos.' }
  }
  if (leituraAtual < leituraCorreta) {
    return { error: 'A leitura atual não pode ser menor que a última leitura correta.' }
  }

  const diasTotal = diffDaysUTC(dataLeituraCorreta, dataCliente)
  const diasErro = diffDaysUTC(dataLeituraCorreta, dataErro)

  if (diasTotal <= 0) return { error: 'A data informada pelo cliente deve ser posterior à última leitura correta.' }
  if (diasErro < 0) return { error: 'A leitura errada não pode ocorrer antes da última leitura correta.' }
  if (diasErro > diasTotal) return { error: 'A leitura errada não pode ocorrer depois da data informada pelo cliente.' }

  const consumoTotal = leituraAtual - leituraCorreta
  const m3Dia = consumoTotal / diasTotal
  const m3Cobrar = m3Dia * diasErro
  const leituraCorrigida = leituraCorreta + m3Cobrar

  return { diasTotal, diasErro, consumoTotal, m3Dia, m3Cobrar, leituraCorrigida }
}

type TarifaCategoria = 'social' | 'residencial' | 'comercial'
type TarifaFaixa = readonly [number, number, number]

type TarifaConfig = {
  fixa: number
  faixas: readonly TarifaFaixa[]
}

export const TARIFAS: Record<TarifaCategoria, TarifaConfig> = {
  social: {
    fixa: 9.01,
    faixas: [
      [20, 3.65, 2.56],
      [Number.POSITIVE_INFINITY, 3.65, 2.56],
    ],
  },
  residencial: {
    fixa: 19.87,
    faixas: [
      [10, 8.03, 5.62],
      [5, 10.28, 7.20],
      [5, 10.46, 7.32],
      [5, 11.57, 8.10],
      [5, 14.23, 9.96],
      [20, 17.02, 11.91],
      [Number.POSITIVE_INFINITY, 18.75, 13.13],
    ],
  },
  comercial: {
    fixa: 30.14,
    faixas: [
      [10, 11.09, 7.76],
      [Number.POSITIVE_INFINITY, 22.69, 15.88],
    ],
  },
}

export type TarifaResult =
  | { error: string }
  | {
      categoria: TarifaCategoria
      consumo: number
      fixa: number
      agua: number
      esgoto: number
      total: number
      socialFallback: boolean
      detalhes: Array<{
        faixa: number
        limite: number
        usado: number
        valorAgua: number
        valorEsgoto: number
        subtotalAgua: number
        subtotalEsgoto: number
      }>
    }

export function calcularTarifa(categoria: TarifaCategoria, consumo: number): TarifaResult {
  if (!Number.isFinite(consumo) || consumo < 0) return { error: 'Informe um consumo válido.' }

  const tabela = TARIFAS[categoria]
  if (!tabela) return { error: 'Categoria não encontrada.' }

  let restante = consumo
  let agua = 0
  let esgoto = 0
  const detalhes: Exclude<TarifaResult, { error: string }>['detalhes'] = []

  tabela.faixas.forEach(([limite, valorAgua, valorEsgoto], index) => {
    if (restante <= 0) return
    const usado = Math.min(limite, restante)
    agua += usado * valorAgua
    esgoto += usado * valorEsgoto
    detalhes.push({
      faixa: index + 1,
      limite,
      usado,
      valorAgua,
      valorEsgoto,
      subtotalAgua: usado * valorAgua,
      subtotalEsgoto: usado * valorEsgoto,
    })
    restante -= usado
  })

  return {
    categoria,
    consumo,
    fixa: tabela.fixa,
    agua: Number(agua.toFixed(2)),
    esgoto: Number(esgoto.toFixed(2)),
    total: Number((agua + esgoto + tabela.fixa).toFixed(2)),
    detalhes,
    socialFallback: categoria === 'social' && consumo > 20,
  }
}

export type ParcelamentoResult =
  | { error: string }
  | {
      valor: number
      entradaPerc: number
      parcelas: number
      entrada: number
      restante: number
      valorParcela: number
      valido: boolean
      minParcela: number
    }

export function calcularParcelamento(valor: number, entradaPerc: number, parcelas: number, minParcela = 20): ParcelamentoResult {
  if (![valor, entradaPerc, parcelas].every(Number.isFinite)) return { error: 'Informe valor, entrada e parcelas.' }
  if (valor <= 0) return { error: 'O valor total deve ser maior que zero.' }
  if (entradaPerc <= 0 || entradaPerc >= 1) return { error: 'A entrada deve estar entre 0 e 100%.' }
  if (parcelas <= 0) return { error: 'O número de parcelas deve ser maior que zero.' }

  const entrada = valor * entradaPerc
  const restante = valor - entrada
  const valorParcela = restante / parcelas
  return { valor, entradaPerc, parcelas, entrada, restante, valorParcela, valido: valorParcela >= minParcela, minParcela }
}

export const PARCELAMENTO_ENTRADAS = [0.30, 0.20, 0.10] as const
export const PARCELAMENTO_OPCOES = [12, 24, 36, 48, 60, 80, 100, 120] as const
export const PARCELAMENTO_MINIMO = 20

type NegociacaoFaixa = readonly [number, number, number, number, number, string]
export const NEGOCIACAO_FAIXAS: readonly NegociacaoFaixa[] = [
  [0, 30, 0, 0, 24, '0 a 30 dias'],
  [31, 60, 0, 0, 24, '31 a 60 dias'],
  [61, 90, 30, 0, 24, '61 a 90 dias'],
  [91, 180, 30, 0, 36, '91 a 180 dias'],
  [181, 365, 30, 10, 48, '181 a 365 dias'],
  [366, 730, 65, 45, 60, '366 a 730 dias'],
  [731, 1095, 75, 55, 60, '731 a 1095 dias'],
  [1096, 1460, 80, 60, 60, '1096 a 1460 dias'],
  [1461, 1825, 90, 65, 60, '1461 a 1825 dias'],
  [1826, Number.POSITIVE_INFINITY, 95, 75, 60, '1826 a ∞ dias'],
]

export type NegociacaoResult =
  | { error: string }
  | {
      dias: number
      faixaLabel: string
      descontoAvista: number
      descontoParcelado: number
      maxParcelas: number
      economiaAvista: number
      valorAvista: number
      economiaParcelado: number
      valorParcelado: number
    }

export function calcularNegociacao(divida: number, dataDivida: Date | null, dataCalculo: Date | null): NegociacaoResult {
  if (!Number.isFinite(divida) || !dataDivida || !dataCalculo) return { error: 'Informe valor e datas válidas.' }
  if (divida <= 0) return { error: 'A dívida precisa ser maior que zero.' }

  const dias = diffDaysUTC(dataDivida, dataCalculo)
  if (dias < 0) return { error: 'A data de cálculo deve ser igual ou posterior à data da dívida.' }

  const faixa = NEGOCIACAO_FAIXAS.find(([min, max]) => dias >= min && dias <= max)
  if (!faixa) return { error: 'Não foi possível localizar a faixa de negociação.' }

  const [, , descontoAvista, descontoParcelado, maxParcelas, faixaLabel] = faixa
  const economiaAvista = divida * (descontoAvista / 100)
  const valorAvista = divida - economiaAvista
  const economiaParcelado = divida * (descontoParcelado / 100)
  const valorParcelado = divida - economiaParcelado

  return {
    dias,
    faixaLabel,
    descontoAvista,
    descontoParcelado,
    maxParcelas,
    economiaAvista,
    valorAvista,
    economiaParcelado,
    valorParcelado,
  }
}

export type { TarifaCategoria }
